if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (this.variantIdInput) this.variantIdInput.disabled = false;

        const captureSubmitter = (e) => {
          const btn = e.target.closest('button[type="submit"], input[type="submit"], button[data-action="buy-now"], button[name="buy_now"]');
          if (btn) this.activeSubmitter = btn;
        };
        this.addEventListener('click', captureSubmitter, true);
        this.addEventListener('pointerdown', captureSubmitter, true);
        this.form?.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton ? (this.submitButton.querySelector('.product-form__submit-text') || this.submitButton.querySelector('span')) : null;

        if (document.querySelector('cart-drawer') && this.submitButton) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.isSubmitting) return;
        const activeEl = (document.activeElement && document.activeElement.closest)
          ? document.activeElement.closest('[type="submit"], button[data-action="buy-now"], button[name="buy_now"]')
          : null;
        const submitter = evt.submitter || this.activeSubmitter || activeEl || this.querySelector('[type="submit"]');
        const isBuyNow = Boolean(
          submitter && (
            submitter.dataset.action === 'buy-now' ||
            submitter.name === 'buy_now' ||
            submitter.classList.contains('action-btn--buy-now') ||
            submitter.classList.contains('quick-buy-btn') ||
            (submitter.id && submitter.id.includes('buy-now'))
          )
        );
        const activeBtn = submitter || this.submitButton;

        if (activeBtn?.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (activeBtn) {
          activeBtn.setAttribute('aria-disabled', true);
          activeBtn.classList.add('loading');
        }
        const spinner = activeBtn ? activeBtn.querySelector('.loading-spinner, .loading__spinner, loading-spinner') : null;
        if (spinner) spinner.classList.remove('hidden');
        else this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const isThemeEditor = Boolean(
          (window.Shopify && window.Shopify.designMode) ||
          (window.self !== window.top)
        );

        let editorCheckoutTab = null;
        if (isBuyNow && isThemeEditor) {
          try {
            editorCheckoutTab = window.open('about:blank', '_blank');
          } catch (err) {
            console.warn('Unable to pre-open checkout tab in editor:', err);
          }
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Explicitly sync quantity from pack selector stepper or form input
        const sectionId = this.dataset.sectionId;
        const rootContext = this.closest('.product') || this.closest('section') || document;
        // Recommendation cards can sit inside the main product's section. They must
        // never inherit that product's pack quantity.
        const isMainProductForm = sectionId && this.form.getAttribute('id') === 'product-form-' + sectionId;
        const qtySource = (isMainProductForm && (
          rootContext.querySelector('#unicarePackQtyInput-' + sectionId) ||
          rootContext.querySelector('.unicare-qty-num-input') ||
          rootContext.querySelector('.quantity__input')
        )) || this.form.querySelector('input[name="quantity"]');
        if (qtySource && parseInt(qtySource.value, 10) > 0) {
          const resolvedQty = parseInt(qtySource.value, 10);
          formData.set('quantity', resolvedQty);
          const formQtyEl = this.form.querySelector('input[name="quantity"]');
          if (formQtyEl) formQtyEl.value = String(resolvedQty);
        }

        if (this.cart && !isBuyNow) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity'), 10) || 1;

        // True Single-Item Buy Now: Route directly to Shopify cart permalink for ONLY this variant & quantity
        if (isBuyNow) {
          if (!variantId) {
            console.error('Missing variant ID for Buy Now');
            if (activeBtn) {
              activeBtn.removeAttribute('aria-disabled');
              activeBtn.classList.remove('loading');
            }
            return;
          }

          const rootUrl = (window.routes && window.routes.root_url) ? window.routes.root_url : '/';
          const cleanRoot = rootUrl.endsWith('/') ? rootUrl : rootUrl + '/';
          const directCheckoutUrl = `${cleanRoot}cart/${variantId}:${quantity}`;

          if (isThemeEditor && editorCheckoutTab) {
            try {
              editorCheckoutTab.location.href = directCheckoutUrl;
              return;
            } catch (err) {
              console.warn('Failed to redirect editor tab:', err);
              window.location.href = directCheckoutUrl;
              return;
            }
          } else {
            window.location.href = directCheckoutUrl;
            return;
          }
        }

        this.isSubmitting = true;
        let itemAdded = false;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (editorCheckoutTab) {
                try { editorCheckoutTab.close(); } catch (e) {}
              }
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = activeBtn?.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              activeBtn?.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            itemAdded = true;
            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              }).catch((error) => console.error('Cart subscriber failed:', error));
            this.error = false;
            if (activeBtn) {
              activeBtn.classList.add('is-added');
              setTimeout(() => {
                activeBtn.classList.remove('is-added');
              }, 2000);
            }
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            if (editorCheckoutTab) {
              try { editorCheckoutTab.close(); } catch (err) {}
            }
            console.error(e);
            if (itemAdded) {
              // The add succeeded. Show the cart instead of inviting a duplicate purchase.
              window.location.href = routes.cart_url;
              return;
            }
            this.handleErrorMessage(window.cartStrings.error);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.isSubmitting = false;
            this.activeSubmitter = null;
            if (activeBtn) {
              activeBtn.classList.remove('loading');
              if (!this.error && !isBuyNow) activeBtn.removeAttribute('aria-disabled');
            }
            if (this.submitButton && this.submitButton !== activeBtn) {
              this.submitButton.classList.remove('loading');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            }
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            spinner?.classList.add('hidden');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
